import { Box, Button } from '@mui/material'
import SaveOutlined from '@mui/icons-material/SaveOutlined'
import { TemplateOutputsRunRateTable } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'
import { RunRateTable } from 'Features/Projects/Scenarios/Templates/TemplateResults/RunRate/RunRateTable'
import { PreferredMethod } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import { useParams } from 'react-router-dom'
import { useUpdateRunRateTable } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.data'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { GridActionBar } from 'Common/Components/GridActionBar'

type RunRateViewProps = {
  data: TemplateOutputsRunRateTable[]
  isLoading: boolean
  /** The method shown, whose template a save rewrites. */
  method: PreferredMethod
}

type RunRateParams = {
  projectId: string
  scenarioId: string
  workcentreId: string
}

/**
 * Each SKU group's cycle, editable on a slider, and the weekly run rates that
 * follow from it. Save sends the edited rows.
 *
 * Save sits in a GridActionBar over the grid and is enabled only while there
 * are unsaved edits. It was a text button above the grid that was always
 * enabled, whether or not anything had changed.
 */
export const RunRateView = ({ data, isLoading, method }: RunRateViewProps) => {
  const { projectId, scenarioId, workcentreId } =
    useParams<RunRateParams>() as RunRateParams

  const [tableRows, setTableRows] =
    useState<TemplateOutputsRunRateTable[]>(data)
  // The rows as last saved. Edits replace the array (RunRateTable maps to a
  // new one), so a reference check is enough to know there are unsaved edits.
  const [savedRows, setSavedRows] = useState(data)
  const dirty = tableRows !== savedRows

  const { mutate: updateCycles, isPending: isUpdating } = useUpdateRunRateTable(
    () => toast.success('Save Successful'),
    () => toast.error('Save Failed')
  )

  const saveChanges = () => {
    const rowsAtSave = tableRows
    updateCycles(
      {
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod: method,
        updateData: rowsAtSave,
      },
      { onSuccess: () => setSavedRows(rowsAtSave) }
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <GridActionBar
        left={
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveOutlined />}
            loading={isUpdating}
            loadingPosition="start"
            disabled={!dirty || isUpdating}
            onClick={saveChanges}
          >
            Save
          </Button>
        }
        hint={dirty ? 'Unsaved changes' : undefined}
      />
      <RunRateTable
        tableRows={tableRows}
        setTableRows={setTableRows}
        isLoading={isLoading || isUpdating}
      />
    </Box>
  )
}
